/* ==================================================================
   First-run checklist (GetStarted.jsx) — shown while the current
   dashboard has no widget. Steps are nested-glass rows with the theme's
   pastel discs; completed steps turn into green checks.
   ================================================================== */

.getStarted {
  max-width: 36rem;
  margin: 6vh auto 0;
}

.getStartedBody {
  padding: 1.75rem;
}

.getStartedHeader {
  text-align: center;
}

.getStartedTitle {
  margin: 0 0 0.4rem;
  font-size: 1.45rem;
  font-weight: 700;
  color: var(--gl-ink);
}

.getStartedSubtitle {
  margin: 0;
  color: var(--gl-muted);
}

.getStartedProgressRow {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1.25rem 0;
}

.getStartedProgressTrack {
  flex: 1 1 auto;
  height: 8px;
  border-radius: 999px;
  background: rgba(28, 35, 52, 0.08);
  overflow: hidden;
}

.getStartedProgressFill {
  height: 100%;
  border-radius: 999px;
  background: var(--gl-accent);
  transition: width 0.4s ease;
}

.getStartedProgressLabel {
  flex: 0 0 auto;
  color: var(--gl-muted);
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.getStartedSteps {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* one selector serves both the Link and the button variant */
.getStartedStep {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  width: 100%;
  padding: 0.8rem 0.9rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.85);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.getStartedStep:hover {
  background: rgba(255, 255, 255, 0.72);
  color: inherit;
  text-decoration: none;
}

.getStartedStepDone {
  background: rgba(222, 240, 226, 0.55);
}

.getStartedStepDone:hover {
  background: rgba(222, 240, 226, 0.8);
}

.getStartedStepIcon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 12px;
  font-size: 1.05rem;
}

.getStartedStepIconDone {
  background: #def0e2;
  color: #2e8f5b;
}

.getStartedTintBlue {
  background: #dde9fb;
  color: #2f5bd7;
}

.getStartedTintAmber {
  background: #f9eeda;
  color: #a5701e;
}

.getStartedTintGreen {
  background: #def0e2;
  color: #2e8f5b;
}

.getStartedTintPurple {
  background: #eae2f8;
  color: #7a4fd1;
}

.getStartedStepBody {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.getStartedStepTitle {
  color: var(--gl-ink);
  font-weight: 600;
  font-size: 0.95rem;
}

.getStartedStepText {
  color: var(--gl-muted);
  font-size: 0.8rem;
}

.getStartedChevron {
  flex: 0 0 auto;
  color: var(--gl-muted);
}

/* Standard dashboard width, shared by the viewer and the editor canvas so
   switching between the two modes never shifts the layout */
.largeContainer {
  max-width: 1600px;
}

/* Per-dashboard "full" width: lift the container cap, keep the side padding.
   Chained with .container so it wins over the breakpoint max-widths. */
.fullWidthContainer:global(.container) {
  max-width: none;
}

/* One sizing model for every dashboard column, shared by the viewer AND the
   editor canvas (EditBoxColumns), so what the canvas shows is what the
   dashboard renders. Below the COLUMNS breakpoint columns stack full width,
   unchanged. Above it, each column asks for its --column-width share of the
   section (set inline: equal shares in a plain section, weight shares in a
   weighted e.g. 2|1 one) but never shrinks under a readable card width:
   columns that no longer fit WRAP onto the next line and share it, instead
   of crushing their widgets — a 6-column dashboard renders 3+3 on a
   mid-size screen, 2+2+2 on a narrow one.

   The COLUMNS breakpoint is 700px, NOT the 992px desktop one that drives
   the navigation (rail vs. mobile dock): a wall tablet in portrait (a
   Shelly Wall Display XL is 752 CSS px wide, an iPad 768, most Android
   tablets ~800) is nowhere near a phone, yet sat in the "phone" layout
   with one stacked column and a lone widget blown up to the full width.
   Two readable columns fit there easily. 700 rather than Bootstrap's 768
   "md": the Shelly sits below 768, and a few px of margin keep it on the
   columns side even when the page is embedded (an iframe panel with a
   scrollbar). A phone in portrait (360–430px) keeps its single column; a
   phone in landscape gets the columns like a tablet, which is the natural
   use of that width. Every column-layout rule (stretch caps, adaptive
   tiles, the editor canvas, the stretched media widgets) keys on this
   same 700px so the tablet renders one coherent desktop-style layout
   under the mobile dock.

   Between the columns and the desktop breakpoints (portrait tablets,
   landscape phones) a line holds AT MOST TWO columns: every column asks
   for half the row, so a 3-column dashboard renders 2+1, a
   4-column one 2+2, and a weighted 2|1 section becomes two equal halves
   instead of its wide column alone on a line. Three columns on a portrait
   tablet (744–834px, iPad mini to iPad Pro 11) would be ~250px each — a
   card width in which the row widgets break (scene names wrap letter by
   letter, setpoint steppers overflow) — and two columns is what Home
   Assistant shows there too. From the desktop breakpoint up the shares
   and the 14rem floor are the historic ones, exactly as before: a
   3-column dashboard right at 992px (~224px per column next to the
   navigation rail) still fits on one line, and wide desktops keep their
   5–6 narrow tile columns — a tablet fix must not reflow anyone's
   desktop. */
.dashboardColumn {
  position: relative;
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
}

@media (min-width: 700px) {
  .dashboardColumn {
    /* half the row whatever the share: a lone 100% column still grows to
       the full row, a 2|1 pair becomes two halves, a third column wraps */
    flex: 1 1 50%;
    min-width: 14rem;
    /* and never wider than half the row either (except a full-width
       column): the odd column that wraps alone keeps the width of the
       pair above it and centers under them (the section row is
       justify-content-center), instead of blowing its widgets up to the
       whole dashboard width. The plain 100% line is the fallback for
       engines without max(). */
    max-width: 100%;
    max-width: max(var(--column-width), 50%);
  }

  /* Bootstrap-row-style negative side margins: the outermost column
     paddings hang outside the container edge, so the outer cards of EVERY
     wrap line stay (near) flush with the page content. The old first/
     last-column padding removal only aligned the single unwrapped line —
     with wrapping it left the lines 15px out of step with each other.
     Capped at the .container side padding (0.75rem in the vendored Tabler
     build), not the full 15px gutter: overhanging the container would
     open a 3px horizontal page scroll. */
  .sectionRow {
    margin-right: -0.75rem;
    margin-left: -0.75rem;
  }
}

/* Desktop: back to the real shares, so 3, 4, 6 columns sit on one line */
@media (min-width: 992px) {
  .dashboardColumn {
    flex-basis: var(--column-width);
    /* A nearly-empty last wrap line (e.g. the lone 6th column) must not
       blow its widgets up to the whole dashboard width: growth is capped
       near a comfortable card width and the line centers. max() keeps wide
       shares — a single 100% column, a 66% weighted one — at their
       intended width; the plain 100% line is the fallback for engines
       without max(). */
    max-width: 100%;
    max-width: max(var(--column-width), 24rem);
  }
}

@media (max-width: 699.98px) {
  .removePadding {
    padding-left: 0;
    padding-right: 0;
  }
}

/* The scene class (below) sets the background-image HERE, but the element
   paints it at zero size: the visible scene is the fixed, full-viewport
   ::before layer, which inherits the image and is composited once — never
   repainted while the dashboard scrolls. The previous mechanism,
   background-attachment: fixed on this (scrolling) element, forced a
   main-thread repaint of the whole gradient on every scroll frame — under
   cards that re-blur their backdrop each frame, that repaint was the
   difference between a fluid scroll and a laggy one on modest hardware
   (see the identical fix on the Activity page, routes/history/style.css).
   Covering the viewport also slides the scene under the nav rail / mobile
   top bar, so their backdrop blur frosts the actual scene — no width or
   margin trick needed for that anymore. */
.dashboardBackground {
  background-size: 0 0;
  min-height: 100vh;
  /* own stacking context so the fixed scene layer can sit at z -1 behind
     the in-flow content but above the element's own background (the
     dark-mode.css opaque .page-main background included) */
  position: relative;
  isolation: isolate;
}

.dashboardBackground::before {
  content: '';
  position: fixed;
  /* longhands, not inset: — it must not silently disappear on the old
     wall tablets (Chromium < 87) */
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
  background-image: inherit;
  background-size: cover;
  background-position: center;
  /* the layer covers areas outside the element's own box (under the nav
     rail): it must never intercept their pointer events */
  pointer-events: none;
}

/* Under the mobile top bar the outer .page is padded (style/index.css):
   pull the box back up and re-pad it, so min-height: 100vh ends exactly at
   the viewport bottom instead of 3.25rem below it (a short dashboard must
   not scroll). The scene itself is viewport-fixed and needs no trick. */
@media (max-width: 991.98px) {
  :global(body.gladys-sidebar-nav) .dashboardBackground {
    margin-top: -3.25rem;
    padding-top: 3.25rem;
  }
}

/* ==================================================================
   "Horizon" glass theme — THE dashboard style, applied unconditionally
   to the whole dashboard page (imposed, not chosen — Gladys ships the
   best default). Everything is driven by the tokens below. Component
   variants (chips, actions, pins, tiles, clock, scenes) live next to
   their component and compose on the same global .glass-theme class,
   kept as the single gate so the theme stays one coherent layer.
   Dark mode: the app darkens through the global inversion filter
   (style/dark-mode.css), so this theme deliberately defines NO dark
   colors — the light glass and its scene invert into their dark
   counterparts like the rest of the app.
   ================================================================== */

:global(.glass-theme) {
  --gl-ink: #1c2334;
  --gl-muted: #7c8396;
  --gl-accent: #3d6df0;
  --gl-radius-lg: 26px;
  --gl-radius-md: 18px;
  --gl-card-bg: rgba(255, 255, 255, 0.52);
  --gl-card-nested-bg: rgba(255, 255, 255, 0.4);
  --gl-surface-strong: rgba(255, 255, 255, 0.78);
  /* floating menus over content (the dashboard switcher's "…" list) */
  --gl-popover-bg: rgba(255, 255, 255, 0.94);
  --gl-border: rgba(255, 255, 255, 0.85);
  --gl-shadow: 0 18px 50px rgba(44, 62, 118, 0.13), 0 2px 8px rgba(44, 62, 118, 0.05);
  --gl-shadow-soft: 0 4px 16px rgba(44, 62, 118, 0.09);
  --gl-icon-tint-bg: #e3ebfb;
  --gl-success-bg: rgba(56, 158, 90, 0.12);
  --gl-success-ink: #2e8f5b;
  --gl-row-border: rgba(44, 62, 118, 0.07);
  color: var(--gl-ink);
}

/* Default scene when the dashboard has no background image of its own:
   soft radial glows over a diagonal gradient — pure CSS, one click.
   An inline background-image set from the dashboard config wins over this. */
/* Scene glows are sized in % (not px) so a scene renders the same
   composition at every element size: the full dashboard, a phone screen,
   and the picker thumbnails that preview it in the settings panel. */
.glassScene {
  background-image: radial-gradient(76% 78% at 12% -10%, rgba(126, 166, 233, 0.55), transparent 60%),
    radial-gradient(62% 72% at 95% 8%, rgba(244, 190, 148, 0.5), transparent 55%),
    radial-gradient(69% 89% at 85% 100%, rgba(187, 160, 228, 0.42), transparent 60%),
    radial-gradient(62% 78% at 10% 105%, rgba(150, 212, 195, 0.4), transparent 55%),
    linear-gradient(160deg, #eef3fb 0%, #eeeaf7 48%, #f7efe6 100%);
}

/* The scene catalog: hand-composed CSS variants of the default scene, picked
   per dashboard (background_scene). No external images — they weigh nothing,
   stay crisp at any resolution, work offline and invert cleanly in dark mode.
   Authored light only, like the whole theme. */

.sceneAurora {
  background-image: radial-gradient(76% 78% at 8% -12%, rgba(112, 205, 178, 0.5), transparent 60%),
    radial-gradient(66% 72% at 92% 4%, rgba(124, 189, 233, 0.5), transparent 55%),
    radial-gradient(69% 89% at 88% 102%, rgba(150, 160, 230, 0.38), transparent 60%),
    radial-gradient(62% 78% at 6% 108%, rgba(140, 220, 210, 0.4), transparent 55%),
    linear-gradient(160deg, #eafaf3 0%, #e9f2fb 52%, #eeecf9 100%);
}

.sceneDusk {
  background-image: radial-gradient(76% 78% at 10% -10%, rgba(246, 173, 128, 0.5), transparent 60%),
    radial-gradient(66% 72% at 94% 6%, rgba(240, 148, 150, 0.45), transparent 55%),
    radial-gradient(69% 89% at 86% 102%, rgba(200, 140, 200, 0.35), transparent 60%),
    radial-gradient(62% 78% at 8% 106%, rgba(248, 205, 150, 0.42), transparent 55%),
    linear-gradient(160deg, #fdf2e6 0%, #fae9ec 50%, #f3e9f6 100%);
}

.sceneForest {
  background-image: radial-gradient(76% 78% at 10% -10%, rgba(150, 210, 150, 0.48), transparent 60%),
    radial-gradient(66% 72% at 94% 6%, rgba(200, 224, 140, 0.42), transparent 55%),
    radial-gradient(69% 89% at 86% 102%, rgba(120, 195, 170, 0.36), transparent 60%),
    radial-gradient(62% 78% at 8% 106%, rgba(190, 220, 165, 0.4), transparent 55%),
    linear-gradient(160deg, #edf7ec 0%, #f0f7e9 52%, #e9f4ef 100%);
}

.sceneLagoon {
  background-image: radial-gradient(76% 78% at 10% -10%, rgba(120, 214, 208, 0.5), transparent 60%),
    radial-gradient(66% 72% at 94% 6%, rgba(126, 186, 240, 0.46), transparent 55%),
    radial-gradient(69% 89% at 86% 102%, rgba(110, 200, 220, 0.36), transparent 60%),
    radial-gradient(62% 78% at 8% 106%, rgba(150, 220, 235, 0.4), transparent 55%),
    linear-gradient(160deg, #e7f8f7 0%, #e9f1fb 52%, #eaf6f9 100%);
}

.sceneSand {
  background-image: radial-gradient(76% 78% at 10% -10%, rgba(236, 205, 156, 0.5), transparent 60%),
    radial-gradient(66% 72% at 94% 6%, rgba(232, 190, 160, 0.44), transparent 55%),
    radial-gradient(69% 89% at 86% 102%, rgba(220, 190, 150, 0.34), transparent 60%),
    radial-gradient(62% 78% at 8% 106%, rgba(240, 220, 180, 0.42), transparent 55%),
    linear-gradient(160deg, #fbf5ea 0%, #f7efe3 52%, #f4ecdf 100%);
}

.sceneLavender {
  background-image: radial-gradient(76% 78% at 10% -10%, rgba(190, 165, 235, 0.5), transparent 60%),
    radial-gradient(66% 72% at 94% 6%, rgba(232, 165, 210, 0.44), transparent 55%),
    radial-gradient(69% 89% at 86% 102%, rgba(160, 150, 230, 0.36), transparent 60%),
    radial-gradient(62% 78% at 8% 106%, rgba(225, 185, 235, 0.4), transparent 55%),
    linear-gradient(160deg, #f2eefb 0%, #f8ecf5 52%, #efeafa 100%);
}

/* the quiet one: near-neutral blue-grays for people who want calm */
.sceneMist {
  background-image: radial-gradient(76% 78% at 10% -10%, rgba(170, 190, 215, 0.4), transparent 60%),
    radial-gradient(66% 72% at 94% 6%, rgba(195, 205, 220, 0.36), transparent 55%),
    radial-gradient(69% 89% at 86% 102%, rgba(175, 190, 205, 0.3), transparent 60%),
    linear-gradient(160deg, #f0f3f7 0%, #eef1f5 52%, #ecf0f4 100%);
}

/* Cards become deep glass. No overflow: hidden — it would clip house-view
   pins near the image edges and in-card dropdown menus; media corners are
   rounded on the images themselves below. */
:global(.glass-theme .card) {
  background: var(--gl-card-bg);
  backdrop-filter: blur(28px) saturate(1.6);
  -webkit-backdrop-filter: blur(28px) saturate(1.6);
  border: 1px solid var(--gl-border);
  border-radius: var(--gl-radius-lg);
  box-shadow: var(--gl-shadow);
  color: var(--gl-ink);
}

:global(.glass-theme .card .card-img-top) {
  border-radius: var(--gl-radius-lg) var(--gl-radius-lg) 0 0;
}

/* dark-mode.css paints .card/.card-header/.card-body with a solid
   !important background: restore the translucent glass with the same
   weight — the inversion filter then derives the dark glass from these
   light values, like the rest of the theme */
:global(.dark-mode .glass-theme .card-header),
:global(.dark-mode .glass-theme .card-body) {
  background-color: transparent !important;
}

:global(.dark-mode .glass-theme .card) {
  background-color: var(--gl-card-bg) !important;
}

:global(.dark-mode .glass-theme .card .card) {
  background-color: var(--gl-card-nested-bg) !important;
}

:global(.glass-theme .card .card) {
  background: var(--gl-card-nested-bg);
  border-radius: var(--gl-radius-md);
  box-shadow: none;
}

/* The header hugs its title: Tabler's 3.5rem min-height centers the small
   uppercase label in a tall band and opens a hole above the card content */
:global(.glass-theme .card-header) {
  border-bottom: none;
  background: transparent;
  min-height: 0;
  padding: 1.125rem 1.5rem 0;
}

/* a header below the media (camera) is the card's last row */
:global(.glass-theme .card-header:last-child) {
  padding-bottom: 1.125rem;
}

:global(.glass-theme .card-header + .card-body) {
  padding-top: 0.75rem;
}

:global(.glass-theme .card-title) {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--gl-muted);
}

:global(.glass-theme h4),
:global(.glass-theme .card p) {
  color: var(--gl-ink);
}

:global(.glass-theme .text-muted),
:global(.glass-theme small) {
  color: var(--gl-muted) !important;
}

/* Value stamps become soft tinted rounded squares */
:global(.glass-theme .stamp) {
  border-radius: 14px;
  width: 46px;
  height: 46px;
  line-height: 46px;
  font-size: 1.15rem;
}

:global(.glass-theme .stamp.bg-green) {
  background: #def0e2 !important;
  color: #2e8f5b !important;
}

:global(.glass-theme .stamp.bg-blue) {
  background: #dde9fb !important;
  color: #3d6df0 !important;
}

:global(.glass-theme .stamp.bg-red) {
  background: #fbe3de !important;
  color: #d4593f !important;
}

:global(.glass-theme .stamp.bg-warning),
:global(.glass-theme .stamp.bg-yellow) {
  background: #f9eeda !important;
  color: #c98a2c !important;
}

/* Bootstrap alerts inside cards become quiet tinted notices — same family
   as the stamps: soft background, saturated ink, no border, no shouting */
:global(.glass-theme .card .alert) {
  border: none;
  border-radius: 14px;
  padding: 0.6rem 0.75rem;
  font-weight: 500;
  font-size: 0.875rem;
  background-color: rgba(28, 35, 52, 0.06);
  color: #667081;
}

:global(.glass-theme .card .alert-danger) {
  background-color: #fbe3de;
  color: #c24a33;
}

:global(.glass-theme .card .alert-warning) {
  background-color: #f9eeda;
  color: #a5701e;
}

:global(.glass-theme .card .alert-info),
:global(.glass-theme .card .alert-primary) {
  background-color: #dde9fb;
  color: #2f5bd7;
}

:global(.glass-theme .card .alert-success) {
  background-color: #def0e2;
  color: #2e8f5b;
}

:global(.glass-theme .card .alert a) {
  color: inherit;
  text-decoration: underline;
  font-weight: 600;
}

/* Tabler's alert-icon variant absolutely positions its icon in a 3rem
   gutter: keep a gutter matching the reduced notice padding */
:global(.glass-theme .card .alert-icon) {
  padding-left: 2.6rem;
}

:global(.glass-theme .card .alert-icon > i) {
  top: 0.7rem;
  left: 0.9rem;
}

/* Tabler's card-alert variant is a full-width square band glued to the card
   edges: float it inside the rounded card instead */
:global(.glass-theme .card .card-alert) {
  margin: 0.75rem 1.5rem 0;
}

/* Page-level alerts (outside any card — dashboard gateway/migration notices,
   activity feed errors) sit directly on the scene: same tinted notice family,
   on an opaque pastel so they stay readable over the glows. The in-card rules
   above are more specific and keep their tighter metrics. */
:global(.glass-theme .alert) {
  border: none;
  border-radius: var(--gl-radius-md);
  font-weight: 500;
  background-color: rgba(255, 255, 255, 0.55);
  color: #667081;
}

:global(.glass-theme .alert-danger) {
  background-color: #fbe3de;
  color: #c24a33;
}

:global(.glass-theme .alert-warning) {
  background-color: #f9eeda;
  color: #a5701e;
}

:global(.glass-theme .alert-info),
:global(.glass-theme .alert-primary) {
  background-color: #dde9fb;
  color: #2f5bd7;
}

:global(.glass-theme .alert-success) {
  background-color: #def0e2;
  color: #2e8f5b;
}

/* Call-to-action of the Gladys Plus recovery codes banner: a solid pill in
   a darker shade of the amber ink of the warning notice it sits in, so the
   white label keeps a 4.5:1 contrast (Tabler's btn-warning is a
   white-on-yellow that clashes with the pastel). Never shrinks: the notice
   text wraps instead. */
.recoveryCodesBannerButton {
  flex-shrink: 0;
  background-color: #8a5d17;
  border: none;
  border-radius: 999px;
  color: #fff;
  font-weight: 600;
  white-space: nowrap;
}

.recoveryCodesBannerButton:hover,
.recoveryCodesBannerButton:focus {
  background-color: #734d13;
  color: #fff;
}

/* Bootstrap badges become the color-dot pill family's plain cousins */
:global(.glass-theme .card .badge) {
  border-radius: 999px;
  padding: 0.3rem 0.7rem;
  font-weight: 600;
  font-size: 0.75rem;
}

:global(.glass-theme .card .badge-success) {
  background-color: #def0e2;
  color: #2e8f5b;
}

:global(.glass-theme .card .badge-danger) {
  background-color: #fbe3de;
  color: #c24a33;
}

:global(.glass-theme .card .badge-warning) {
  background-color: #f9eeda;
  color: #a5701e;
}

:global(.glass-theme .card .badge-info) {
  background-color: #dde9fb;
  color: #2f5bd7;
}

:global(.glass-theme .card .badge-secondary) {
  background-color: rgba(28, 35, 52, 0.06);
  color: #667081;
}

/* Tabler's bg-* badge utilities join the family; dark-mode.css special-cases
   .badge.bg-warning with a dark gold and no filter — restore the pastel and
   the double inversion the other badges get */
:global(.glass-theme .card .badge.bg-warning),
:global(.dark-mode .glass-theme .card .badge.bg-warning) {
  background-color: #f9eeda !important;
  color: #a5701e !important;
  filter: invert(100%) hue-rotate(180deg);
}

:global(.glass-theme .card .badge.bg-warning) {
  filter: none;
}

/* Neutral solid/outline buttons (music transport, camera live, energy nav)
   become quiet ink-tinted pills; dark-mode.css paints btn-secondary with a
   solid !important background, hence the counter-weight */
:global(.glass-theme .card .btn-secondary),
:global(.glass-theme .card .btn-outline-secondary),
:global(.dark-mode .glass-theme .card .btn-secondary) {
  background-color: rgba(28, 35, 52, 0.07) !important;
  border: none;
  color: var(--gl-ink);
  border-radius: 999px;
  font-weight: 600;
  box-shadow: none;
}

:global(.glass-theme .card .btn-secondary:hover),
:global(.glass-theme .card .btn-outline-secondary:hover),
:global(.dark-mode .glass-theme .card .btn-secondary:hover) {
  background-color: rgba(28, 35, 52, 0.13) !important;
  color: var(--gl-ink);
}

/* solid accent buttons (camera pause…) become ink pills; dark-mode.css puts
   .btn-primary on the double-invert list, which would keep the authored ink
   navy on the inverted (dark) card — drop the filter so the pill inverts
   with the theme into a light pill, like the active dashboard tab */
:global(.glass-theme .card .btn-primary) {
  background-color: var(--gl-ink);
  border: none;
  border-radius: 999px;
  font-weight: 600;
}

:global(.dark-mode .glass-theme .card .btn-primary) {
  filter: none;
}

/* soft amber pill (alarm arming countdown cancel) */
:global(.glass-theme .card .btn-outline-warning) {
  background-color: #f9eeda;
  border: none;
  color: #a5701e;
  border-radius: 999px;
  font-weight: 600;
}

/* Start/action buttons become soft pills */
:global(.glass-theme .btn-outline-success) {
  background: var(--gl-success-bg);
  border: none;
  color: var(--gl-success-ink);
  border-radius: 999px;
  font-weight: 600;
  padding: 0.35rem 1rem;
}

/* Loading states join the theme: the stock spinner (a solid framework-blue
   arc floating bare over the scene) becomes a thin accent ring on a small
   frosted puck, and the loading-border pulse of refreshing cards blinks in
   the accent instead of the old framework blue. Dark mode still comes from
   the global inversion filter. */
:global(.glass-theme .loader) {
  width: 3.25rem;
  height: 3.25rem;
  color: var(--gl-accent);
  background: var(--gl-surface-strong);
  backdrop-filter: blur(18px) saturate(1.5);
  -webkit-backdrop-filter: blur(18px) saturate(1.5);
  border: 1px solid var(--gl-border);
  border-radius: 999px;
  box-shadow: var(--gl-shadow-soft);
}

/* ::before is the faint full track, ::after the spinning arc — both sized
   down from the framework's 2.5rem/3px to fit centered inside the puck */
:global(.glass-theme .loader)::before,
:global(.glass-theme .loader)::after {
  width: 1.625rem;
  height: 1.625rem;
  margin: -0.8125rem 0 0 -0.8125rem;
  border-width: 2px;
}

:global(.glass-theme .card.loading-border) {
  animation: glass-loading-border-blink 2s infinite;
}

@keyframes glass-loading-border-blink {
  50% {
    border-color: rgba(61, 109, 240, 0.6);
  }
}

/* Remaining tables (device lists) lose their heavy borders */
:global(.glass-theme .table td),
:global(.glass-theme .table th) {
  border-top: none;
}

:global(.glass-theme .table tbody tr + tr td) {
  border-top: 1px solid var(--gl-row-border);
}

/* Page controls become quiet frosted pills */
:global(.glass-theme .page-options .btn),
:global(.glass-theme .dropdown > .btn) {
  border-radius: 999px;
  border: 1px solid var(--gl-border);
  background: var(--gl-card-bg);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  font-weight: 600;
  color: var(--gl-muted);
}

:global(.glass-theme .dropdown > .btn) {
  color: var(--gl-ink);
}

:global(.glass-theme .page-options .btn-outline-primary) {
  color: var(--gl-accent);
}

/* dark-mode.css double-inverts outline buttons to preserve their light
   look; under glass they are already frosted pills — they must invert
   with the theme like every other glass surface, or they stay white
   over the dark scene */
:global(.dark-mode .glass-theme .btn-outline-primary),
:global(.dark-mode .glass-theme .btn-outline-danger) {
  filter: none;
}

/* Tablet dock: frosted pill, active tab inverted.
   The frost (background + backdrop blur) is painted by the ::before layer
   below, NOT by the bar itself, on purpose: the "…" overflow menu is a DOM
   descendant of the bar, and an element with backdrop-filter is a backdrop
   root — per spec (and strictly in Chromium) the backdrop-filter of a
   descendant then only samples what is painted INSIDE that ancestor, never
   the page behind it. With the blur on the bar, the menu's own blur was a
   silent no-op on desktop Chrome/Edge: the cards' text showed through its
   78% white, crisp (forum feedback). Safari does not isolate, which is why
   the same CSS looked opaque on iOS. */
:global(.glass-theme) .dashboardTabs {
  border: 1px solid var(--gl-border);
  border-radius: 999px;
  padding: 5px;
  box-shadow: var(--gl-shadow-soft);
  gap: 4px;
}

:global(.glass-theme) .dashboardTabs::before {
  content: '';
  position: absolute;
  /* longhands, not inset: — Chromium < 87 on the old wall tablets */
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  /* behind the pills, inside the bar's own stacking context (see the
     z-index on .dashboardTabs) */
  z-index: -1;
  border-radius: inherit;
  background: var(--gl-card-bg);
  backdrop-filter: blur(24px) saturate(1.5);
  -webkit-backdrop-filter: blur(24px) saturate(1.5);
  pointer-events: none;
}

:global(.glass-theme) .dashboardTab {
  border: none;
  background: transparent;
  height: 2.6rem;
  padding: 0 0.9rem;
  color: var(--gl-muted);
}

:global(.glass-theme) .dashboardTabsIconsOnly .dashboardTab {
  width: 2.6rem;
  padding: 0;
}

:global(.glass-theme) .dashboardTabActive {
  background: var(--gl-ink);
  color: #fff;
  box-shadow: 0 4px 14px rgba(28, 35, 52, 0.35);
}

.dashboardTabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  /* Stacked above the page content so the overflow menu opens on top of
     the dashboard cards. The stacking context this creates is also what
     keeps the glass variant's ::before frost layer (z-index: -1) behind the
     pills instead of behind the whole page. */
  position: relative;
  z-index: 10;
}

.dashboardTab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 2.375rem;
  padding: 0 0.875rem;
  border-radius: 999px;
  border: 1px solid rgba(0, 40, 100, 0.12);
  background-color: #fff;
  color: #495057;
  font-size: 1rem;
  white-space: nowrap;
  flex: 0 0 auto;
}

.dashboardTabName {
  font-size: 0.875rem;
  font-weight: 600;
  /* one endless name must not eat the whole row */
  max-width: 11rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dashboardTabsOverflow {
  display: inline-flex;
  flex: 0 0 auto;
}

/* Mobile dock (DashboardTabs scrollable layout): the pill track scrolls
   sideways with a hidden scrollbar — same treatment as the app's other
   sideways rows (.hz-tabs, .groupChips) — instead of collapsing pills
   behind the "…" menu. */
.dashboardTabsScrollable {
  flex-wrap: nowrap;
  align-items: center;
}

.dashboardTabsTrack {
  position: relative;
  display: flex;
  gap: inherit;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  /* programmatic centering of the active pill glides instead of jumping;
     old engines without scroll-behavior simply jump, which is fine */
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  /* A scroll container clips painted overflow: without this breathing room
     the active pill's glass shadow is cut at the track's edge. The negative
     margin gives the room back so the bar keeps its height. The overhang
     also acts as a small touch halo above the slim capsule (a slightly-high
     thumb still grabs the bar) — kept at the shadow gutter ON PURPOSE: the
     dock floats over the page in a z-100 fixed header, so anything larger
     covers the tail of the last widget with an invisible scroller. Beyond
     it, DashboardSwiper's 32px guard strip is a dead zone: a higher
     near-miss does nothing rather than switching dashboards. */
  padding: 18px 0;
  margin: -18px 0;
}

.dashboardTabsTrack::-webkit-scrollbar {
  display: none;
}

/* Edge fades — the scrollbar is hidden, so they are the signal that more
   pills sit past the edge. Toggled from scroll position: an edge fully
   scrolled to shows plain, not faded. */
.dashboardTabsTrackFadeStart {
  -webkit-mask-image: linear-gradient(to right, transparent, #000 28px);
  mask-image: linear-gradient(to right, transparent, #000 28px);
}

.dashboardTabsTrackFadeEnd {
  -webkit-mask-image: linear-gradient(to left, transparent, #000 28px);
  mask-image: linear-gradient(to left, transparent, #000 28px);
}

.dashboardTabsTrackFadeStart.dashboardTabsTrackFadeEnd {
  -webkit-mask-image: linear-gradient(to right, transparent, #000 28px, #000 calc(100% - 28px), transparent);
  mask-image: linear-gradient(to right, transparent, #000 28px, #000 calc(100% - 28px), transparent);
}

.dashboardTabsMenu {
  max-height: 60vh;
  overflow-y: auto;
  min-width: 13rem;
}

.dashboardTabsMenuIcon {
  margin-right: 0.5rem;
}

/* The bar and the header actions share one row at every width — vertical
   space is precious, actions never cost a line. nowrap matters: with
   wrapping allowed the bar would be measured at full width (the actions
   already pushed below) and the priority+ trim would never give their
   space back */
.dashboardHeader {
  flex-wrap: nowrap;
  /* The switcher stays pinned while the dashboard scrolls under it — the
     pill track's backdrop blur frosts the passing cards. Kept floating a
     small gap from the edge so it reads as a hovering capsule, not a bar. */
  position: sticky;
  top: 0.75rem;
  z-index: 100;
  /* only the capsules catch the pointer: the flex-grown gap between the
     pill track and the action buttons is empty space, and widgets passing
     under it must stay clickable/tappable */
  pointer-events: none;
}

.dashboardHeader .dashboardTabs,
.dashboardHeader :global(.page-options) {
  pointer-events: auto;
}

/* Below the desktop breakpoint the switcher becomes a bottom dock, within
   thumb reach (iOS style: the reachable edge is the bottom one). fixed, not
   sticky: the header sits at the top of the flow, sticky-bottom would never
   engage. Content gets bottom padding so the last row scrolls clear of it. */
@media (max-width: 991.98px) {
  .dashboardHeader {
    position: fixed;
    top: auto;
    left: 0.75rem;
    right: 0.75rem;
    bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
    margin: 0;
    /* --dock-lift (set from the VisualViewport API in DashboardPage) is how
       much of the layout viewport's bottom is currently overlaid by the
       browser's animating toolbars: without it iOS Safari transiently hides
       the dock under its own bottom bar while scrolling */
    transform: translateY(calc(-1 * var(--dock-lift, 0px)));
  }

  /* Opt-in, NOT on .dashboardBackground itself: the scene is shared by every
     wallpaper page (dashboard, editor, new dashboard, the zone form of Plans)
     but only the dashboard carries the switcher dock. On the others the
     padding was pure dead space at the bottom of the page.
     An explicit class rather than :has(.dashboardHeader): :has() is unknown to
     Chromium < 105 / Safari < 15.4 / Firefox < 121, and an unknown pseudo-class
     invalidates the whole rule — the dock clearance would silently disappear on
     exactly the old wall tablets this dock is designed for. */
  .withBottomDock {
    padding-bottom: calc(4.5rem + env(safe-area-inset-bottom, 0px));
  }

  /* The "…" menu opens upward from the dock and spans the whole pill track.
     Anchored to its own .dropdown wrapper (the "…" button) it could open
     outside the screen: dropdown-menu-right hangs the panel leftward from
     the button, which sits near the LEFT edge when only a pill or two are
     visible, and a long dashboard name widens the nowrap panel past the
     viewport. position: static on the wrapper hands the containing block
     to the track (position: relative), whose box is always on-screen. */
  .dashboardTabsOverflow {
    position: static;
  }

  .dashboardTabsMenu {
    top: auto;
    bottom: calc(100% + 8px);
    left: 0;
    right: 0;
  }

  /* the panel width is now the track's, not the content's: one endless
     dashboard name must clip, not push the panel past the viewport */
  .dashboardTabsMenu :global(.dropdown-item) {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* The dock floats over arbitrary scenes (photos included): a slightly
     deeper shadow than the desktop bar keeps it visually anchored (forum
     feedback). The action pills alongside get the same one so the row
     keeps one voice. */
  :global(.glass-theme) .dashboardHeader .dashboardTabs,
  :global(.glass-theme) .dashboardHeader :global(.page-options .btn) {
    box-shadow: 0 10px 30px rgba(28, 35, 52, 0.24), 0 2px 8px rgba(28, 35, 52, 0.12);
  }
}

.dashboardHeaderTabs {
  flex: 1 1 auto;
  min-width: 0;
}

.dashboardHeader :global(.page-options) {
  flex: 0 0 auto;
}

/* Actions are round icon pills with exactly the bar's outer height
   (pill height + bar padding + border), so the row reads as one line */
.dashboardHeader :global(.page-options .btn) {
  align-items: center;
  justify-content: center;
  width: calc(2.6rem + 12px);
  height: calc(2.6rem + 12px);
  padding: 0;
  border-radius: 999px;
  font-size: 1.05rem;
}

/* Horizon glass theme: the overflow menu is a glass surface like the bar
   it opens from, not a stock white dropdown. It floats over the cards'
   text, not over the scene: a popover needs a much denser glass than a
   card to stay readable, whatever sits under it — the blur (which now
   works, see the bar's ::before) softens the backdrop, the near-opaque
   white guarantees the legibility even where backdrop-filter is missing. */
:global(.glass-theme) .dashboardTabsMenu {
  background: var(--gl-popover-bg);
  backdrop-filter: blur(28px) saturate(1.6);
  -webkit-backdrop-filter: blur(28px) saturate(1.6);
  border: 1px solid var(--gl-border);
  border-radius: var(--gl-radius-md);
  box-shadow: var(--gl-shadow);
  padding: 6px;
}

:global(.glass-theme) .dashboardTabsMenu :global(.dropdown-item) {
  border-radius: 10px;
  color: var(--gl-ink);
  font-weight: 500;
}

/* an ink tint, not more white: white on the near-opaque panel is invisible */
:global(.glass-theme) .dashboardTabsMenu :global(.dropdown-item):hover {
  background: rgba(44, 62, 118, 0.08);
}

:global(.glass-theme) .dashboardTabsMenu :global(.dropdown-item.active) {
  background: var(--gl-ink);
  color: #fff;
}

:global(.glass-theme) .dashboardTabsMenuIcon {
  color: var(--gl-accent);
}

:global(.glass-theme) .dashboardTabsMenu :global(.dropdown-item.active) .dashboardTabsMenuIcon {
  color: #fff;
}

/* dark-mode.css paints .dropdown-menu with a solid !important background:
   restore the glass surface with the same weight (see the card rules above) */
:global(.dark-mode .glass-theme) .dashboardTabsMenu {
  background-color: var(--gl-popover-bg) !important;
}

/* Tablet mode keeps the compact icon dock */
.dashboardTabsIconsOnly .dashboardTab {
  width: 2.375rem;
  padding: 0;
}

/* On the whole mobile dock only the active pill keeps its name — the rest
   shrink to icon dots so more of them fit in the scrollable track. The full
   dock breakpoint on purpose (not just phones): the dock must not change
   its look between a portrait and a landscape phone. */
@media (max-width: 991.98px) {
  .dashboardTab:not(.dashboardTabActive) {
    width: 2.375rem;
    padding: 0;
  }

  .dashboardTab:not(.dashboardTabActive) .dashboardTabName {
    display: none;
  }
}

.dashboardTab:hover {
  color: #467fcf;
  text-decoration: none;
}

.dashboardTabActive {
  background-color: #467fcf;
  border-color: #467fcf;
  color: #fff;
}

.dashboardTabActive:hover {
  color: #fff;
}

/* Boxes of a stretchable type absorb the remaining height of their column so
   sections with columns of different heights don't leave blank space */
.stretchableBox {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.stretchableBox > div {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Tile boxes (room temperature/humidity) center their content in the
   extra height instead of leaving it under the card */
.stretchableTile > div {
  justify-content: center;
}

/* Stretch is a shock absorber, not an elastic: a stretchable box absorbs
   the free height of its column only up to this cap. Beyond it the column
   simply ends earlier and the dashboard background breathes, instead of a
   photo — or a house illustration card — blown up to several times its
   natural size. Columns breakpoint only — below it columns stack and
   heights are natural. */
@media (min-width: 700px) {
  .stretchableBox {
    max-height: 65vh;
  }
}

/* On large screens a stretched value tile's height is imposed by its
   column, so its wrapper can be a size container: the tile content adapts
   to the real rendered height (see components/boxs/roomTile.css). The
   container is the padding-less wrapper, not the card, because container
   queries evaluate against the content box. The min-height keeps a
   sensible tile when every box of a section is a value tile. Not applied
   below the columns breakpoint, where columns stack and heights are
   natural. */
@media (min-width: 700px) {
  .adaptiveTile {
    container-type: size;
    min-height: 6rem;
  }

  /* A value tile is a number and a label: its own stretch cap is far below
     the generic 65vh one (which fits media boxes, whose image fills the
     card). 16rem is where the big-tile layout reaches its full type scale
     (20cqh = the 3.2rem clamp cap) — any taller is only empty glass, e.g.
     a lone tile next to a very tall column. Chained selector: it must beat
     .stretchableBox whatever the order of these blocks. */
  .stretchableBox.adaptiveTile {
    max-height: 16rem;
  }
}

@media (min-width: 992px) {
  .smallButtonOnBigScreen {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    min-width: 1.625rem;
    line-height: 1.33333333;
    border-radius: 3px;
  }
}

.collapse {
  max-height: 0;
  transition: max-height 0.25s ease-out;
  overflow: hidden;
}

.showCollapse {
  max-height: 500px;
  overflow: auto;
  transition: max-height 0.5s ease-in;
}

.tabletModeDiv {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  visibility: hidden;
  /* Visibility is not interpolable: delay it so the panel stays visible while collapsing */
  transition: opacity 0.3s ease, grid-template-rows 0.3s ease, visibility 0s linear 0.3s;
  padding: 0rem;
}

/* The row is sized from the content, so the panel never overlaps the dashboard below */
.tabletModeDivContent {
  min-height: 0;
  overflow: hidden;
}

/* The collapse animation needs the overflow: hidden above, which would clip
   the large glass shadow into a faint dark rectangle behind the card (square
   corners, hard bottom edge). This panel card goes flat — the glass surface
   and border carry it. */
:global(.glass-theme) .tabletModeDivContent :global(.card) {
  box-shadow: none;
}

.tabletModeDivOpen {
  visibility: visible;
  opacity: 1;
  grid-template-rows: 1fr;
  /* Opening stays immediate */
  transition-delay: 0s, 0s, 0s;
}

/* Old wall tablets without backdrop-filter support get a more opaque
   glass so text stays readable over the scene */
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  :global(.glass-theme .card) {
    background: rgba(255, 255, 255, 0.92);
  }

  :global(.glass-theme) {
    --gl-surface-strong: rgba(255, 255, 255, 0.95);
  }
}

/* ============================================================
   Horizon device list: the Tabler table rows of the devices widgets
   become soft pill rows of the scene/actions family — icon in a tinted
   badge, one rounded surface per row. Almost CSS-only: the 25+ feature
   components keep their <tr> markup — the one exception is the legacy
   Bootstrap `mr-2` dropped from the read-only sensor icon, the single
   feature row that carried one (see SensorDeviceFeature.jsx).
   ============================================================ */

:global(.glass-theme .device-list-table) {
  border-collapse: separate;
  border-spacing: 0 8px;
  padding: 0 12px 8px;
  width: 100%;
}

:global(.glass-theme .device-list-table tr td) {
  background-color: var(--gl-card-nested-bg);
  border-top: 1px solid var(--gl-border);
  border-bottom: 1px solid var(--gl-border);
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: var(--gl-ink);
  font-weight: 500;
  vertical-align: middle;
}

:global(.glass-theme .device-list-table tr td:first-child) {
  border-left: 1px solid var(--gl-border);
  border-radius: 14px 0 0 14px;
  padding-left: 0.625rem;
  width: 1%;
  white-space: nowrap;
}

:global(.glass-theme .device-list-table tr td:last-child) {
  border-right: 1px solid var(--gl-border);
  border-radius: 0 14px 14px 0;
  padding-right: 0.75rem;
}

/* the feature icon sits in a tinted badge, like the scene rows */
:global(.glass-theme .device-list-table tr td:first-child > .fe) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 12px;
  background-color: var(--gl-icon-tint-bg);
  color: var(--gl-accent);
  font-size: 1rem;
}

/* pressing a tappable row gives immediate feedback — the whole-row tap is a
   coarse-pointer affordance only (the JS handler is gated the same way):
   with a mouse, the switch itself is a comfortable target and a click on
   the device name must not be a live write */
@media (pointer: coarse) {
  :global(.glass-theme .device-list-table .device-row-tappable:active td) {
    background-color: var(--gl-icon-tint-bg);
  }
}

/* The weather forecast rows scroll sideways on purpose — their slots keep a
   readable floor rather than being crushed into a narrow card. What they must
   not do is carry a permanent scrollbar across the card: it reads as a broken
   layout, which is how it was reported. Same treatment as the app's other
   sideways rows (.hz-tabs, .groupChips): the bar is hidden, and a thin one
   comes back on hover or keyboard focus for mouse users. */
:global(.glass-theme .weather-forecast-scroll) {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

:global(.glass-theme .weather-forecast-scroll::-webkit-scrollbar) {
  display: none;
}

:global(.glass-theme .weather-forecast-scroll:hover),
:global(.glass-theme .weather-forecast-scroll:focus-within) {
  scrollbar-width: thin;
}

:global(.glass-theme .weather-forecast-scroll:hover::-webkit-scrollbar),
:global(.glass-theme .weather-forecast-scroll:focus-within::-webkit-scrollbar) {
  display: block;
  height: 4px;
}

:global(.glass-theme .weather-forecast-scroll:hover::-webkit-scrollbar-thumb),
:global(.glass-theme .weather-forecast-scroll:focus-within::-webkit-scrollbar-thumb) {
  background: rgba(28, 35, 52, 0.2);
  border-radius: 4px;
}

/* Each widget row becomes an independent flex row (the <tr>/<td> markup of
   the 25+ feature components stays untouched). As a real table, the three
   columns were SHARED by every row of the card: one wide control (a shutter
   button group, a setpoint capsule) claimed that width for the whole control
   column, and every name of the card was crushed against it — the truncated
   MQTT names of the community report ("noms d'appareils MQTT tronqués sur
   mobile"), while narrow rows showed a large dead gap. Flex frees each row
   to split itself: the control keeps exactly its natural width, the name
   gets every remaining pixel of ITS row, and instead of a nowrap ellipsis
   it WRAPS on several lines like the scene rows (overflow-wrap: anywhere
   breaks even single-token names). The adaptive option controls
   (acAdaptiveControls) still collapse to dropdowns on real overflow: a
   control cell never shrinks, so an oversized button group still overflows
   the .table-responsive box, which is what the coordinator measures. */
:global(.glass-theme .device-widget-table),
:global(.glass-theme .device-widget-table tbody) {
  display: block;
}

/* A block box has no border-spacing: the `0 8px` of .device-list-table is
   gone, and `tr + tr` below only brings back the gap BETWEEN pills. That
   spacing also framed the rows — 8px above the first, 8px under the last,
   on top of the table's own 8px bottom padding. This widget has no
   .card-body and the card header stops at padding-bottom: 0, so the top one
   was the only air under the title. Both are restored here as padding. */
:global(.glass-theme .device-widget-table) {
  padding-top: 8px;
  padding-bottom: 16px;
}

:global(.glass-theme .device-widget-table tr) {
  display: flex;
  /* stretch (default): the three cells paint one equal-height pill */
}

:global(.glass-theme .device-widget-table tr + tr) {
  margin-top: 8px;
}

/* the width: 1% of the shared table sizing would crush the icon cell now
   that width feeds flex-basis */
:global(.glass-theme .device-widget-table tr td:first-child) {
  flex: 0 0 auto;
  width: auto;
  display: flex;
  align-items: center;
}

/* Column flex, not block: the light row stacks two divs (name + summary)
   in this cell; plain text rows put their text in an anonymous item. */
:global(.glass-theme .device-widget-table tr td:nth-child(2)) {
  flex: 1 1 auto;
  /* The floor under the name: without it, a control wide enough (the shutter
     buttons before their compact mode) squeezed this cell to a letter per
     line — overflow-wrap: anywhere breaks ANYWHERE, including every
     character. 6rem still wraps long names, but on readable chunks, and a
     control that truly cannot coexist with it overflows the wrapper again,
     which is the signal acAdaptiveControls listens for. */
  min-width: 6rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-wrap: anywhere;
}

/* The control never grows past its natural width, and never wraps: nowrap
   makes the natural width of a switch, stepper or button group its flex
   minimum too, so those keep their designed size and hit area whatever the
   name's length. shrink: 1 (not 0) matters for the one elastic control, the
   slider: its track can give width back to a long name down to the
   .rangeInput min-width floor, instead of imposing its full track on a
   narrow card. */
:global(.glass-theme .device-widget-table tr td:last-child) {
  flex: 0 1 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  white-space: nowrap;
}

/* An option select is a form-control, so the framework gives it width: 100%
   — resolved against a shrinkable flex cell, a long name could crush it to
   its arrow. Intrinsic width instead, floored so the current value stays
   readable. */
:global(.glass-theme .device-widget-table td select.form-control) {
  width: auto;
  min-width: 4.5rem;
}

/* Only the adaptive controls' fallback is also capped: it stands in for a
   button group the coordinator judged too wide, so it must stay compact. The
   selects that are always the control of their row (camera preset, fan,
   vacuum and pilot-wire modes) show a real label — "Recall a preset…" — and
   a cap would clip it, which is what this PR is fixing everywhere else. */
:global(.glass-theme .device-widget-table td .adaptive-option-select select.form-control) {
  max-width: 9rem;
}

/* The slider row (MultiLevelDeviceFeature) wraps its control in a Bootstrap
   .col: its 15px grid gutters are 30px of dead width taken from the name. */
:global(.glass-theme .device-widget-table td .col) {
  padding-left: 0;
  padding-right: 0;
}

/* The "no recent value" badge is the exception to nowrap: folded on two
   balanced lines (forum suggestion) it stops stealing half the row from
   the name next to it. */
:global(.glass-theme .device-widget-table .no-recent-value-badge) {
  white-space: normal;
  max-width: 7rem;
  line-height: 1.25;
  text-align: center;
}

/* A text feature is the other exception: its value is free-form text of any
   length (a sun-protection advice, a TV app name...), not a control with a
   designed width. Kept on one line by the nowrap above, a long sentence
   pushed the pill past the card and gave the widget a horizontal scrollbar
   (community report "débordement horizontal"). It wraps like the name
   next to it — overflow-wrap: anywhere so a single long token (a URL, a
   serial number) breaks too — and stays right-aligned, as every value is. */
:global(.glass-theme .device-widget-table .text-device-value) {
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: right;
}

/* Compact card mode. Below ~20rem (3+ desktop columns under ~1200px, an
   iPad Pro in portrait) a card is too narrow for icon + a 6rem name + a
   setpoint capsule or a temperature value on one line: the control was
   pushed past the card and clipped, and a name at its floor still broke
   mid-word ("Temperatu / re"). The row wraps instead: a control that no
   longer fits next to a 6rem name drops under it, right-aligned, on the
   full width of the row — the setpoint capsule and long values do, a
   switch or a short badge stays on the name's line. A container query on
   the card (device-widget, declared on the .table-responsive wrapper in
   components/boxs/device-in-room), not on the viewport: a two-column
   tablet card is wide and keeps the one-line rows. Wide cards are left
   exactly as they are, so acAdaptiveControls keeps collapsing an oversized
   button group to a dropdown there; in a compact card the group first drops
   under the name, and only overflows (which the coordinator measures) if it
   is wider than the whole row. */
@container device-widget (max-width: 20rem) {
  /* The pill moves from the three cells to the row: painted on the cells,
     it breaks apart as soon as one of them wraps to a second line. */
  :global(.glass-theme .device-widget-table tr) {
    flex-wrap: wrap;
    background-color: var(--gl-card-nested-bg);
    border: 1px solid var(--gl-border);
    border-radius: 14px;
  }

  /* Tighter chrome, ~30px given back to the name and the control: at the
     narrowest desktop column (a 14rem column, 223px card) that is what lets
     a switch or a short badge keep the name's line. */
  :global(.glass-theme .device-widget-table) {
    padding-left: 8px;
    padding-right: 8px;
  }

  :global(.glass-theme .device-widget-table tr td) {
    background-color: transparent;
    border: none;
    border-radius: 0;
    padding: 0.375rem;
  }

  /* The color picker's structural second row (see the td[colspan] reset
     below) is not a pill either: painted on the row it would be an empty
     bordered line under every ungrouped color row. An explicit class on the
     <tr> rather than :has(td[colspan]): :has() is unknown to Firefox < 121,
     which already evaluates @container, and an unknown pseudo-class drops
     the whole rule. */
  :global(.glass-theme .device-widget-table tr.device-color-picker-row) {
    background-color: transparent;
    border: none;
    border-radius: 0;
    margin-top: 0;
  }

  :global(.glass-theme .device-widget-table tr td:first-child) {
    padding-left: 0.375rem;
  }

  /* The basis is the name's floor: a control that leaves it less than 6rem
     wraps under it. The huge grow factor keeps a control that shares the
     line at its natural width (the free space goes 999:1 to the name)
     while a wrapped control, alone on its line, gets the whole line. */
  :global(.glass-theme .device-widget-table tr td:nth-child(2)) {
    flex: 999 1 6rem;
  }

  :global(.glass-theme .device-widget-table tr td:last-child) {
    flex: 1 1 auto;
    margin-left: auto;
    padding-right: 0.5rem;
  }

  @media (pointer: coarse) {
    :global(.glass-theme .device-list-table .device-row-tappable:active td) {
      background-color: transparent;
    }

    :global(.glass-theme .device-widget-table .device-row-tappable:active) {
      background-color: var(--gl-icon-tint-bg);
    }
  }
}

/* Structural rows are not feature pills: ColorDeviceFeature always renders
   a second tr with one colspan cell holding the (d-none while closed) color
   picker — without this reset it paints as an empty ghost pill. */
:global(.glass-theme .device-list-table tr td[colspan]) {
  background-color: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  /* in the flex rows of the widget (see below) this lone cell is both
     first and last child: it must span its whole row, not shrink-to-fit */
  flex: 1 1 auto;
  width: 100%;
}

/* ============================================================
   Touch targets (viewer): on coarse-pointer devices every widget
   control reaches the ~44px Apple HIG floor. pointer: coarse means
   the PRIMARY pointer is a finger (phones, tablets) — hybrid laptops
   keep the compact mouse sizing.
   ============================================================ */

@media (pointer: coarse) {
  :global(.device-row-tappable) {
    cursor: pointer;
  }
}

@media (pointer: coarse) {
  /* generic floor for widget buttons (scene start, camera live, music
     transport, energy nav, shutter groups, adaptive option pairs…) */
  :global(.glass-theme .card .btn) {
    min-height: 2.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  :global(.glass-theme .card .btn-group .btn) {
    min-width: 2.75rem;
  }

  /* Tabler switches grow to a finger-sized toggle */
  :global(.glass-theme .custom-switch-indicator) {
    width: 3.25rem;
    height: 1.75rem;
  }

  /* The knob offsets are measured from the PADDING box of the track (its
     1px border is outside them): top must be computed from the center, not
     copied from the visual gap, or the knob sits 1px low (3px of air above,
     0 below). Same on the horizontal axis so both rest positions keep the
     same 2px of track visible around the knob. */
  :global(.glass-theme .custom-switch-indicator)::before {
    width: 1.5rem;
    height: 1.5rem;
    top: calc(50% - 0.75rem);
    left: 1px;
  }

  :global(.glass-theme .custom-switch-input:checked ~ .custom-switch-indicator)::before {
    left: calc(100% - 1.5rem - 1px);
  }

  /* the selector pills round up from 2.6rem to the floor, and the header
     action pills keep matching the bar height */
  :global(.glass-theme) .dashboardTab {
    height: 2.75rem;
  }

  :global(.glass-theme) .dashboardTabsIconsOnly .dashboardTab {
    width: 2.75rem;
  }

  .dashboardHeader :global(.page-options .btn) {
    width: calc(2.75rem + 12px);
    height: calc(2.75rem + 12px);
  }
}

@media (pointer: coarse) and (max-width: 991.98px) {
  .dashboardTab:not(.dashboardTabActive) {
    width: 2.75rem;
  }
}

/* ============================================================
   Horizon controls inside the device rows: the Tabler button
   groups (AC modes, thermostat modes, shutters, fan speeds…)
   become iOS-like segmented controls — a soft track with a white
   active segment — and the setpoint steppers become capsules.
   ============================================================ */

/* .hz-segmented is the opt-in marker for any widget button group
   (energy consumption periods, cost/kWh toggle…) */
:global(.glass-theme .device-list-table .btn-group),
:global(.glass-theme .hz-segmented) {
  background-color: rgba(28, 35, 52, 0.07);
  border-radius: 999px;
  padding: 3px;
  gap: 3px;
  align-items: center;
  /* the control cells are py-0: the track brings its own breathing room */
  margin: 4px 0;
}

:global(.glass-theme .device-list-table .btn-group .btn),
:global(.glass-theme .hz-segmented .btn) {
  background: transparent !important;
  border: none;
  border-radius: 999px !important;
  color: var(--gl-muted);
  font-weight: 600;
  box-shadow: none;
}

/* inside a segmented track the whole track is the touch zone: segments
   stay at the iOS segmented height instead of the 44px solo-button floor */
@media (pointer: coarse) {
  :global(.glass-theme .device-list-table .btn-group .btn),
  :global(.glass-theme .hz-segmented .btn) {
    min-height: 2.25rem;
  }
}

:global(.glass-theme .device-list-table .btn-group .btn:hover),
:global(.glass-theme .hz-segmented .btn:hover) {
  color: var(--gl-ink);
}

:global(.glass-theme .device-list-table .btn-group .btn:disabled),
:global(.glass-theme .hz-segmented .btn:disabled) {
  opacity: 0.45;
}

:global(.glass-theme .device-list-table .btn-group .btn.active),
:global(.glass-theme .device-list-table .btn-group .btn.active:disabled),
:global(.glass-theme .hz-segmented .btn.active) {
  background-color: #fff !important;
  color: var(--gl-ink);
  box-shadow: 0 2px 8px rgba(28, 35, 52, 0.18);
  opacity: 1;
}

/* the dropdown fallback of narrow cards joins the family */
:global(.glass-theme .device-list-table select.form-control) {
  border-radius: 999px;
  border: 1px solid var(--gl-border);
  background-color: #fff;
  font-weight: 600;
  color: var(--gl-ink);
}

/* the energy widget's period date picker joins it — the input is readonly
   (it only opens the calendar, typing would pop the mobile keyboard), so it
   must keep the interactive look, not the framework's grayed [readonly] one */
:global(.glass-theme .card .react-datepicker-wrapper .form-control) {
  border-radius: 999px;
  border: 1px solid var(--gl-border);
  background-color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  color: var(--gl-ink);
  cursor: pointer;
}

/* ---------- Mobile pager (DashboardSwiper + DashboardSkeleton) ---------- */

/* The track the pager translates: the current dashboard in normal flow,
   the neighbor skeletons absolutely posed one page to each side */
.pagerTrack {
  position: relative;
}

/* The ±16px gutter between pages must match PAGE_GUTTER_PX in
   DashboardSwiper.jsx — the committed slide translates by width + gutter */
.pagerNeighbor {
  position: absolute;
  width: 100%;
  pointer-events: none;
}

.pagerNeighborPrevious {
  left: calc(-100% - 16px);
}

.pagerNeighborNext {
  left: calc(100% + 16px);
}

/* Skeleton cards: the glass card frame comes from .card (glass-theme), this
   only lays out the placeholder content inside it */
.skeletonCard {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 1.125rem 1.5rem;
}

/* same voice as .card-title, since it shows the widget's configured title */
.skeletonTitle {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--gl-muted, #7c8396);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.skeletonTitleBar {
  height: 0.6rem;
  width: 42%;
  border-radius: 999px;
  background: rgba(28, 35, 52, 0.12);
}

.skeletonBlock {
  flex: 1;
  min-height: 1.5rem;
  border-radius: var(--gl-radius-md, 18px);
  background: linear-gradient(
    100deg,
    rgba(28, 35, 52, 0.06) 40%,
    rgba(28, 35, 52, 0.11) 50%,
    rgba(28, 35, 52, 0.06) 60%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;
}

/* chips boxes are bare pills, not cards — mirror that in the skeleton */
.skeletonChipsRow {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.skeletonChip {
  height: 2rem;
  width: 6.5rem;
  border-radius: 999px;
  background: var(--gl-card-bg, rgba(255, 255, 255, 0.55));
  border: 1px solid var(--gl-border, rgba(255, 255, 255, 0.85));
}

@keyframes skeleton-shimmer {
  from {
    background-position: 120% 0;
  }

  to {
    background-position: -80% 0;
  }
}

/* Mobile bottom dock (see .dashboardHeader above): the "…" menu opens from
   inside the dock track's own backdrop root, where a nested backdrop-filter
   makes Chrome misrender the panel (content bleeding through it). Solid
   background, no blur — the inversion filter still derives the dark variant.
   Kept at the END of the file: it must out-cascade the base glass menu rule
   and the dark-mode !important one. */
@media (max-width: 991.98px) {
  :global(.glass-theme) .dashboardTabsMenu,
  :global(.dark-mode .glass-theme) .dashboardTabsMenu {
    background-color: #fff !important;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
}
